import React, { useState, useRef, useEffect, useCallback } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';

interface InlineEditNotesProps {
  value: string;
  onSave: (value: string) => void;
}

const InlineEditNotesComponent: React.FC<InlineEditNotesProps> = ({
  value: initialValue,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    if (value !== initialValue) {
      onSave(value);
    }
  }, [value, initialValue, onSave]);

  // Click outside handler
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        handleSave();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleSave]);

  // Focus effect
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setValue(initialValue || '');
    }
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  if (!isEditing) {
    return (
      <div
        style={(styles as any).inlineEdit?.notesContainer || {}}
        onDoubleClick={handleDoubleClick}
        title="Double-click to edit"
      >
        {value ? (
          <p style={styles.table.notesDescription} title={value}>
            {value}
          </p>
        ) : (
          <span style={(styles as any).inlineEdit?.placeholder || {}}>No notes</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={(styles as any).inlineEdit?.editContainer || {}}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        style={(styles as any).inlineEdit?.notesTextarea || {}}
        placeholder="Add notes..."
        rows={3}
      />
    </div>
  );
};

export const InlineEditNotes = React.memo(InlineEditNotesComponent);